import React, { useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box } from '@mui/material'

/** Height of the phones' restaurant / branch bar under the header. */
export const CONTEXT_BAR_HEIGHT = 44

/**
 * What the bar adds under the header right now (`44px`, or unset), kept on <html>: the admin
 * shell's spacer under its header grows by it, so this app's pages (which start under that spacer)
 * clear the bar. A copy of resto-app-admin `components/Header/ContextBar.js` — keep them in step.
 */
export const CONTEXT_BAR_CSS_VAR = '--app-context-bar-height'

/**
 * Publishes the bar's height while `active`. Every header showing the bar holds a token in one set
 * on `window` (the shell and the embedded apps are separate bundles on one page, and two headers
 * can be up at once), so the space stays while any of them still shows it.
 */
export const useContextBarSpace = (active) => {
  useLayoutEffect(() => {
    if (!active || typeof window === 'undefined') return undefined
    const holders = (window.__APP_CONTEXT_BAR_HOLDERS__ = window.__APP_CONTEXT_BAR_HOLDERS__ || new Set())
    const token = {}
    holders.add(token)
    document.documentElement.style.setProperty(CONTEXT_BAR_CSS_VAR, `${CONTEXT_BAR_HEIGHT}px`)
    return () => {
      holders.delete(token)
      if (holders.size === 0) document.documentElement.style.removeProperty(CONTEXT_BAR_CSS_VAR)
    }
  }, [active])
}

/**
 * Whether the header (the element `ref` goes on) sits in a dialog or another MUI modal. A full-screen
 * editor there clears only the header's 50px row, with its own fixed offsets, so the names stay in
 * the row there — a page's header gets the bar, Save / Cancel or not. Known before the first paint.
 */
export const useInsideModal = () => {
  const ref = useRef(null)
  const [inside, setInside] = useState(false)
  useLayoutEffect(() => {
    setInside(Boolean(ref.current?.closest('.MuiModal-root')))
  }, [])
  return [ref, inside]
}

/**
 * Phones: the header row has no room for the restaurant / branch names, so they get a full-width
 * row of their own under it (RestaurantBranchSelector `bar`). Rendered inside the header's app bar,
 * which grows by CONTEXT_BAR_HEIGHT (NavBarRoot `contextBar`).
 */
const ContextBar = ({ children }) => (
  <Box
    sx={{
      height: CONTEXT_BAR_HEIGHT,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1,
      borderTop: 1,
      borderColor: 'divider',
      color: 'text.primary',
    }}
  >
    {children}
  </Box>
)

ContextBar.propTypes = {
  children: PropTypes.node,
}

export default ContextBar
